<template>
  <div>
    <v-layout justify-center>
      <img
        class="responsive-img"
        src="/static/invite_new_users.png"
        style="max-width: 738px"
      >
    </v-layout>
    <v-layout justify-center>
      <div class="mt-4">
        <div class="headline mb-3">
          {{ $t('userManagementInviteNewDescr_1') }}
        </div>
        <div class="subheading grey--text text--darken-1 mb-1">
          {{ $t('userManagementInviteNewDescr_2') + '.' }}
          {{ $t('userManagementInviteNewDescr_3') + '.' }}
        </div>
      </div>
    </v-layout>
    <!-- Allowed email domains -->
    <v-slide-y-reverse-transition>
      <div
        v-if="hasEmailDomainLimitations"
        class="mt-3"
      >
        <div class="subheading mb-1">
          {{ $t('userManagementInviteAllowedDomains') }}
        </div>
        <v-layout wrap>
          <span
            v-for="domain in allowedEmailDomains"
            :key="domain"
            class="msr--text mr-1 subheading"
          >{{ domain }}</span>
        </v-layout>
      </div>
    </v-slide-y-reverse-transition>
    <!-- User Email to invite -->
    <v-layout
      align-center
      class="mt-4 mb-2"
    >
      <v-slide-x-transition>
        <v-icon
          v-if="isEmailValid"
          color="success"
          class="mr-2 mb-1"
          medium
        >
          check
        </v-icon>
      </v-slide-x-transition>
      <v-text-field
        v-model="rawEmail"
        :label="$t('userManagementInviteNewEmailLabel')"
        :placeholder="emailPlaceholder"
        clearable
        :color="isEmailValid ? 'success' : 'primary'"
        :rules="[rules.noGermanUmlaute, rules.email]"
        @click:clear="() => reset()"
        @keyup.esc="reset()"
      />
    </v-layout>
    <v-layout wrap>
      <v-btn
        class="msr mr-0 ml-auto"
        :dark="isEmailValid"
        :disabled="!isEmailValid"
        :loading="busy"
        round
        @click="inviteNewUser(parsedEmail)"
      >
        Einladen
        <v-icon right>
          send
        </v-icon>
      </v-btn>
    </v-layout>
    <v-slide-y-transition>
      <v-layout
        v-if="pendingUserInvitations.length > 0"
        column
        class="mt-4 grey--text text--darken-1"
      >
        <div class="subheading mb-2">
          {{ $t('userManagementInvitationsPending') }}:
        </div>
        <ul>
          <li
            v-for="invitation in pendingUserInvitations"
            :key="invitation.id"
            class="mb-1"
          >
            <v-layout wrap>
              <span class="body-2 mr-2">{{
                invitation.created | dayjs('DD.MM.YYYY')
              }}</span>
              <span>{{ invitation.payload.email }}</span>
              <span
                v-if="invitation.could_not_yet_get_delivered"
                class="ml-auto warning--text caption"
              >
                {{ $t('userManagementInvitedEmailNotYetDelivered') }}
              </span>
            </v-layout>
          </li>
        </ul>
      </v-layout>
    </v-slide-y-transition>
  </div>
</template>

<script>
// https://stackoverflow.com/questions/14440444
const EMAIL_REGEX = /^([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]{2,})$/gi;
const GERMAN_UMLAUTS = ['ä', 'ö', 'ü', 'ß'];

export default {
  data() {
    return {
      busy: false,
      rawEmail: '',
      // Note: iOS ignores the newline here, so multiline examples
      //   would just be concatenated.
      emailPlaceholder: '\n' + 'max@mustermann.de',
      rules: {
        noGermanUmlaute: (v) =>
          !GERMAN_UMLAUTS.some((umlaut) => v.toLowerCase().includes(umlaut)) ||
          this.$t('invalidEmailPleaseAvoidGermanUmlauts'),
        email: (v) => EMAIL_REGEX.test(v) || this.$t('invalidEmail'),
      },
    };
  },
  computed: {
    pendingUserInvitations() {
      return this.$store.state.common.pendingUserInvitations;
    },
    allowedEmailDomains() {
      const workspace = this.$userprofile.managed_workspace;
      return !!workspace ? workspace.email_domains : [];
    },
    hasEmailDomainLimitations() {
      return this.allowedEmailDomains.length > 0;
    },
    /** Email that has been extracted from the text using a regex. */
    parsedEmail() {
      return ((this.rawEmail || '').match(EMAIL_REGEX) || [''])[0];
    },
    /** Whether email has passed domain validation. */
    isEmailValid() {
      if (!this.parsedEmail) {
        return false;
      }
      if (!this.hasEmailDomainLimitations) {
        return true;
      }
      for (const domain of this.allowedEmailDomains) {
        if (this.parsedEmail.endsWith(domain)) {
          return true;
        }
      }
      return false;
    },
    emailInvalidWithDomainLimitations() {
      return this.parsedEmail && !this.isEmailValid;
    },
  },
  created() {
    this.fetchPendingUserInvitations();
  },
  methods: {
    fetchPendingUserInvitations() {
      return this.$store.dispatch('LIST_PENDING_USER_INVITATIONS');
    },
    reset() {
      this.busy = false;
      this.rawEmail = '';
    },
    inviteNewUser(email) {
      this.busy = true;
      this.$api
        .inviteNewUser({email: email})
        .then((response) => {
          const message =
            this.$t('userManagementInviteSentSuccess') + ': ' + email;
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 10000,
          });
          this.reset();

          this.fetchPendingUserInvitations();
          // Update users list as the invitation should have created one.
          // Also redirect to new User so the admin can set permissions.
          this.$store.dispatch('LIST_MANAGED_USERS').then(() => {
            const userId = response.data.user_id;
            this.$router.push({
              name: 'userManagementUser',
              params: {userId: userId},
            });
          });
        })
        .catch((error) => {
          console.error(error);

          let alertType = 'alert-error';
          let errorMessage = this.$t('genericFailedInfo');
          let timeout = 3000;

          // Demo users are not allowed to invite
          if (error.response.status === 412) {
            errorMessage = this.$t('userManagementDemoUserCannotInvite');
            timeout = 8000;
          }

          // Limit reached
          if (error.response.status === 402) {
            errorMessage = this.$t(
              'userManagementMaxAmountManagedUsersReached'
            );
            timeout = 8000;
          }

          // Email already in use
          if (
            error.response.status === 409 &&
            error.response.data &&
            error.response.data.reason
          ) {
            alertType = 'alert-warning';
            errorMessage =
              this.$t(error.response.data.reason) +
              ': ' +
              error.response.data.email;
            timeout = 10000;
          }

          this.$bus.$emit(alertType, {
            message: errorMessage,
            timeout: timeout,
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style scoped>
.responsive-img {
  width: 100%;
  height: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/UserManagement/InviteUsers.vue